import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePageMeta } from "@/lib/pageMeta";

const SERVICES_OPTIONS = [
    "Brand Care",
    "Identidade Visual",
    "PackPost",
    "Design Gráfico",
    "Planejamento & Estratégia",
    "Não tenho certeza ainda",
];

const CARGO_OPTIONS = [
    "Fundador(a) / CEO",
    "Sócio(a) / Proprietário(a)",
    "Gerente / Coordenador(a)",
    "Marketing / Comunicação",
    "Autônomo(a) / Liberal",
    "Outro",
];

const TEMPO_OPTIONS = [
    "Menos de 1 ano",
    "1 ano",
    "2 a 3 anos",
    "4 a 5 anos",
    "Mais de 5 anos",
    "Ainda não lançada",
];

const TOM_OPTIONS = [
    "Profissional",
    "Descontraído / Casual",
    "Elegante / Sofisticado",
    "Educativo / Didático",
    "Moderno / Jovem",
    "Outro",
];

const formatPhone = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 11);
    if (!digits) return "";
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

const formatCurrency = (val) => {
    const digits = val.replace(/\D/g, "");
    if (!digits) return "";
    const number = parseInt(digits, 10);
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 0,
    }).format(number);
};

const hasStep32 = (servico) => {
    return [
        "PackPost",
        "Design Gráfico",
        "Planejamento & Estratégia",
        "Não tenho certeza ainda",
    ].includes(servico);
};

// Componente visual de Radio
const RadioItem = ({ label, selected, onSelect }) => (
    <button
        type="button"
        onClick={onSelect}
        className="flex items-center gap-2.5 text-left cursor-pointer group text-[13px] text-ink select-none py-0.5"
    >
        <span
            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-all ${
                selected
                    ? "bg-brand ring-2 ring-brand/20"
                    : "bg-[#D1D5DB] group-hover:bg-[#9CA3AF]"
            }`}
        >
            {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
        </span>
        <span className={selected ? "font-medium text-ink" : "text-ink/80"}>{label}</span>
    </button>
);

// Componente visual de Checkbox
const CheckboxItem = ({ label, checked, onToggle }) => (
    <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2.5 text-left cursor-pointer group text-[13px] text-ink select-none py-0.5"
    >
        <span
            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-all ${
                checked
                    ? "border-brand bg-brand text-white"
                    : "border-ink/40 bg-white group-hover:border-brand"
            }`}
        >
            {checked && (
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
            )}
        </span>
        <span className={checked ? "font-medium text-ink" : "text-ink/80"}>{label}</span>
    </button>
);

const INITIAL_BRIEFING = {
    // Brand Care
    brandCare_identidade: "",
    brandCare_redes: [],
    brandCare_redesOutra: "",
    brandCare_publico: "",
    // Identidade Visual
    idVisual_situacao: "",
    idVisual_escopo: [],
    idVisual_referencias: "",
    // PackPost 3
    packPost_prioridade: [],
    packPost_prioridadeOutra: "",
    packPost_quantidade: "",
    packPost_tipoConteudo: [],
    // PackPost 3.2
    packPost_materiais: [],
    packPost_temIdVisual: "",
    packPost_tom: "Profissional",
    packPost_objetivo: [],
    // Design Gráfico 3
    design_tipoMaterial: [],
    design_materialOutro: "",
    // Design Gráfico 3.2
    design_temBrief: "",
    design_temIdVisual: "",
    // Planejamento & Estratégia 3
    plan_dificuldade: "",
    plan_temIdVisual: "",
    plan_objetivo6m: "",
    plan_publico: "",
    // Planejamento & Estratégia 3.2
    plan_concorrente1: "",
    plan_concorrente2: "",
    plan_concorrente3: "",
    plan_comoEncontram: "",
    // Não tenho certeza ainda
    geral_necessidade: "",
};

export const ContactPage = () => {
    usePageMeta({
        title: "Contato | Briefing",
        description: "Inicie seu projeto com a Rock Fox Studio.",
        path: "/contato",
    });

    const [step, setStep] = useState(1); // 1 | 2 | 3 | 3.2 | 4 | 5
    const [status, setStatus] = useState("idle"); // "idle" | "loading" | "error"
    const [errorMessage, setErrorMessage] = useState("");
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        // Etapa 1
        nome: "",
        email: "",
        telefone: "",
        empresa: "",
        cargo: "",
        tempoEmpresa: "",
        // Etapa 2
        servico: "",
        budget: "",
        // Etapa 3 & 3.2 específicas
        ...INITIAL_BRIEFING,
        // Etapa 4
        confirmoDados: false,
        autorizoContato: false,
    });

    const clearError = (field) => {
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const updated = { ...prev };
            delete updated[field];
            return updated;
        });
    };

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        clearError(field);
    };

    const toggleArrayField = (field, item) => {
        setFormData((prev) => {
            const list = prev[field] || [];
            const exists = list.includes(item);
            const updated = exists ? list.filter((i) => i !== item) : [...list, item];
            return { ...prev, [field]: updated };
        });
        clearError(field);
    };

    const handlePhoneChange = (e) => {
        const masked = formatPhone(e.target.value);
        updateField("telefone", masked);
    };

    const handleBudgetChange = (e) => {
        const masked = formatCurrency(e.target.value);
        updateField("budget", masked);
    };

    const selectServico = (servico) => {
        setFormData((prev) => {
            // Se o serviço clicado for diferente do que estava selecionado,
            // limpa completamente os campos do briefing anterior!
            if (prev.servico !== servico) {
                return {
                    ...prev,
                    servico,
                    ...INITIAL_BRIEFING,
                };
            }
            return prev;
        });
        clearError("servico");
        setErrors({});
    };

    const validateStep1 = () => {
        const errs = {};
        if (!formData.nome.trim()) {
            errs.nome = "Informe seu nome completo.";
        } else if (formData.nome.trim().length < 3) {
            errs.nome = "O nome deve ter pelo menos 3 caracteres.";
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            errs.email = "Informe seu e-mail.";
        } else if (!emailRegex.test(formData.email.trim())) {
            errs.email = "Informe um e-mail válido (ex: contato@suaempresa.com).";
        }

        const rawPhone = formData.telefone.replace(/\D/g, "");
        if (!formData.telefone.trim()) {
            errs.telefone = "Informe seu telefone.";
        } else if (rawPhone.length < 10) {
            errs.telefone = "Informe um telefone válido com DDD (ex: 11 98765-4321).";
        }

        if (!formData.empresa.trim()) {
            errs.empresa = "Informe o nome da sua empresa ou marca.";
        }

        if (!formData.cargo) {
            errs.cargo = "Selecione seu cargo/função.";
        }

        if (!formData.tempoEmpresa) {
            errs.tempoEmpresa = "Selecione o tempo de existência.";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const validateStep2 = () => {
        const errs = {};
        if (!formData.servico) {
            errs.servico = "Selecione o serviço desejado.";
        }
        if (!formData.budget.trim()) {
            errs.budget = "Informe a estimativa de budget para o projeto.";
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const validateStep3 = () => {
        const errs = {};
        const s = formData.servico;

        if (s === "Brand Care") {
            if (!formData.brandCare_identidade) errs.brandCare_identidade = "Selecione uma opção.";
            if (formData.brandCare_redes.length === 0 && !formData.brandCare_redesOutra.trim()) {
                errs.brandCare_redes = "Selecione ao menos uma rede social.";
            }
            if (!formData.brandCare_publico.trim()) errs.brandCare_publico = "Descreva seu público-alvo principal.";
        } else if (s === "Identidade Visual") {
            if (!formData.idVisual_situacao) errs.idVisual_situacao = "Selecione a situação da sua identidade.";
            if (formData.idVisual_escopo.length === 0) errs.idVisual_escopo = "Selecione ao menos uma opção de escopo.";
            if (!formData.idVisual_referencias.trim()) errs.idVisual_referencias = "Informe referências ou marcas que gosta.";
        } else if (s === "PackPost") {
            if (formData.packPost_prioridade.length === 0 && !formData.packPost_prioridadeOutra.trim()) {
                errs.packPost_prioridade = "Selecione ao menos uma rede prioritária.";
            }
            if (!formData.packPost_quantidade) errs.packPost_quantidade = "Selecione a quantidade de posts por mês.";
            if (formData.packPost_tipoConteudo.length === 0) errs.packPost_tipoConteudo = "Selecione o tipo de conteúdo desejado.";
        } else if (s === "Design Gráfico") {
            if (formData.design_tipoMaterial.length === 0 && !formData.design_materialOutro.trim()) {
                errs.design_tipoMaterial = "Selecione ao menos um tipo de material.";
            }
        } else if (s === "Planejamento & Estratégia" || s === "Não tenho certeza ainda") {
            if (!formData.plan_dificuldade.trim()) errs.plan_dificuldade = "Compartilhe sua principal dúvida/dificuldade.";
            if (!formData.plan_temIdVisual) errs.plan_temIdVisual = "Selecione uma opção.";
            if (!formData.plan_objetivo6m.trim()) errs.plan_objetivo6m = "Informe seu maior objetivo nos próximos 6 meses.";
            if (!formData.plan_publico.trim()) errs.plan_publico = "Descreva seu público-alvo.";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const validateStep32 = () => {
        const errs = {};
        const s = formData.servico;

        if (s === "PackPost") {
            if (formData.packPost_materiais.length === 0) errs.packPost_materiais = "Selecione uma opção sobre materiais.";
            if (!formData.packPost_temIdVisual) errs.packPost_temIdVisual = "Selecione se já possui identidade visual.";
            if (formData.packPost_objetivo.length === 0) errs.packPost_objetivo = "Selecione ao menos um objetivo principal.";
        } else if (s === "Design Gráfico") {
            if (!formData.design_temBrief) errs.design_temBrief = "Selecione se tem referências ou brief.";
            if (!formData.design_temIdVisual) errs.design_temIdVisual = "Selecione sobre a identidade visual.";
        } else if (s === "Planejamento & Estratégia" || s === "Não tenho certeza ainda") {
            if (!formData.plan_concorrente1.trim() && !formData.plan_concorrente2.trim() && !formData.plan_concorrente3.trim()) {
                errs.plan_concorrentes = "Cite ao menos um concorrente.";
            }
            if (!formData.plan_comoEncontram.trim()) errs.plan_comoEncontram = "Descreva como seus clientes te encontram.";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const validateStep4 = () => {
        const errs = {};
        if (!formData.confirmoDados) {
            errs.confirmoDados = "Você precisa confirmar que as informações estão corretas.";
        }
        if (!formData.autorizoContato) {
            errs.autorizoContato = "Você precisa autorizar o contato da Rockfox Studio.";
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleNext = () => {
        if (step === 1) {
            if (!validateStep1()) return;
            setErrors({});
            setStep(2);
        } else if (step === 2) {
            if (!validateStep2()) return;
            setErrors({});
            setStep(3);
        } else if (step === 3) {
            if (!validateStep3()) return;
            setErrors({});
            if (hasStep32(formData.servico)) {
                setStep(3.2);
            } else {
                setStep(4);
            }
        } else if (step === 3.2) {
            if (!validateStep32()) return;
            setErrors({});
            setStep(4);
        }
    };

    const prevStep = () => {
        setErrors({});
        if (step === 3.2) {
            setStep(3);
        } else if (step === 4) {
            setStep(hasStep32(formData.servico) ? 3.2 : 3);
        } else if (step === 3) {
            setStep(2);
        } else if (step === 2) {
            setStep(1);
        }
    };

    const handleSubmitBriefing = async () => {
        if (!validateStep4()) return;

        setStatus("loading");
        setErrorMessage("");

        try {
            const payload = {
                Nome: formData.nome || "Não informado",
                "E-mail": formData.email || "Não informado",
                Telefone: formData.telefone || "Não informado",
                "Empresa / Marca": formData.empresa || "Não informado",
                "Cargo / Função": formData.cargo || "Não informado",
                "Tempo de Empresa": formData.tempoEmpresa || "Não informado",
                "Serviço de Interesse": formData.servico || "Não informado",
                "Budget Disponível": formData.budget || "Não informado",
                // Detalhes conforme o serviço
                ...(formData.servico === "Brand Care" && {
                    "Brand Care - Identidade Visual": formData.brandCare_identidade,
                    "Brand Care - Redes Sociais": [...formData.brandCare_redes, formData.brandCare_redesOutra].filter(Boolean).join(", "),
                    "Brand Care - Público-Alvo": formData.brandCare_publico,
                }),
                ...(formData.servico === "Identidade Visual" && {
                    "Identidade Visual - Situação": formData.idVisual_situacao,
                    "Identidade Visual - Escopo": formData.idVisual_escopo.join(", "),
                    "Identidade Visual - Referências": formData.idVisual_referencias,
                }),
                ...(formData.servico === "PackPost" && {
                    "PackPost - Redes Prioritárias": [...formData.packPost_prioridade, formData.packPost_prioridadeOutra].filter(Boolean).join(", "),
                    "PackPost - Quantidade de Posts": formData.packPost_quantidade,
                    "PackPost - Tipo de Conteúdo": formData.packPost_tipoConteudo.join(", "),
                    "PackPost - Materiais/Conteúdo": formData.packPost_materiais.join(", "),
                    "PackPost - Tem Identidade": formData.packPost_temIdVisual,
                    "PackPost - Tom de Comunicação": formData.packPost_tom,
                    "PackPost - Objetivos": formData.packPost_objetivo.join(", "),
                }),
                ...(formData.servico === "Design Gráfico" && {
                    "Design Gráfico - Materiais": [...formData.design_tipoMaterial, formData.design_materialOutro].filter(Boolean).join(", "),
                    "Design Gráfico - Brief/Referências": formData.design_temBrief,
                    "Design Gráfico - Identidade Estabelecida": formData.design_temIdVisual,
                }),
                ...((formData.servico === "Planejamento & Estratégia" || formData.servico === "Não tenho certeza ainda") && {
                    "Planejamento - Principal Dificuldade": formData.plan_dificuldade,
                    "Planejamento - Identidade Visual": formData.plan_temIdVisual,
                    "Planejamento - Maior Objetivo 6 Meses": formData.plan_objetivo6m,
                    "Planejamento - Público-Alvo": formData.plan_publico,
                    "Planejamento - Concorrentes": [formData.plan_concorrente1, formData.plan_concorrente2, formData.plan_concorrente3].filter(Boolean).join(" | "),
                    "Planejamento - Como Clientes Encontram": formData.plan_comoEncontram,
                }),
                "Autorizou Contato": formData.autorizoContato ? "Sim" : "Não",
                _subject: `Novo Briefing: ${formData.nome || "Cliente"} - ${formData.servico || "Serviço"} (${formData.empresa || "Empresa"})`,
                _template: "table",
                _captcha: "false",
            };

            const response = await fetch("https://formsubmit.co/ajax/adm@rockfoxstudio.com", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setStatus("idle");
                setStep(5);
            } else {
                throw new Error("Falha no envio do briefing");
            }
        } catch (err) {
            setStatus("error");
            setErrorMessage("Não foi possível enviar o formulário automaticamente. Tente novamente ou nos envie um e-mail em adm@rockfoxstudio.com.");
        }
    };

    return (
        <section className="relative flex min-h-[calc(100vh-100px)] w-full items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
            <div className="w-full max-w-[490px]">
                <AnimatePresence mode="wait">
                    {/* ETAPA 1 */}
                    {step === 1 && (
                        <motion.div
                            key="step-1"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 1/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[20%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[28px] sm:text-[32px] font-bold leading-tight text-ink tracking-tight">
                                Vamos começar?
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Precisamos conhecer você e sua empresa para criar a melhor solução.
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Nome completo: *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Digite seu nome"
                                        value={formData.nome}
                                        onChange={(e) => updateField("nome", e.target.value)}
                                        className={`w-full rounded-[14px] border ${
                                            errors.nome ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.nome && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.nome}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        E-mail: *
                                    </label>
                                    <input
                                        type="email"
                                        placeholder="Digite seu e-mail"
                                        value={formData.email}
                                        onChange={(e) => updateField("email", e.target.value)}
                                        className={`w-full rounded-[14px] border ${
                                            errors.email ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.email && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.email}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Telefone: *
                                    </label>
                                    <input
                                        type="tel"
                                        placeholder="(00) 98765-4321"
                                        value={formData.telefone}
                                        onChange={handlePhoneChange}
                                        className={`w-full rounded-[14px] border ${
                                            errors.telefone ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.telefone && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.telefone}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Empresa/Marca (nome) *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Digite o nome da Empresa/Marca"
                                        value={formData.empresa}
                                        onChange={(e) => updateField("empresa", e.target.value)}
                                        className={`w-full rounded-[14px] border ${
                                            errors.empresa ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.empresa && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.empresa}</p>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                    <div className="flex flex-col">
                                        <label className="text-[13px] font-medium text-ink min-h-[38px] flex items-end pb-1.5">
                                            Cargo/Função *
                                        </label>
                                        <div className="relative">
                                            <select
                                                value={formData.cargo}
                                                onChange={(e) => updateField("cargo", e.target.value)}
                                                className={`w-full h-[42px] appearance-none rounded-[14px] border ${
                                                    errors.cargo ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                                } bg-white px-4 py-2 pr-8 text-sm ${
                                                    formData.cargo ? "text-ink" : "text-ink/40"
                                                } focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                            >
                                                <option value="" disabled>
                                                    Ex: Gerente
                                                </option>
                                                {CARGO_OPTIONS.map((opt) => (
                                                    <option key={opt} value={opt} className="text-ink">
                                                        {opt}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink/60">
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>
                                        {errors.cargo && (
                                            <p className="mt-1 text-[11px] font-medium text-red-500">{errors.cargo}</p>
                                        )}
                                    </div>

                                    <div className="flex flex-col">
                                        <label className="text-[13px] font-medium text-ink min-h-[38px] flex items-end pb-1.5 leading-tight">
                                            Quanto tempo sua empresa existe? *
                                        </label>
                                        <div className="relative">
                                            <select
                                                value={formData.tempoEmpresa}
                                                onChange={(e) => updateField("tempoEmpresa", e.target.value)}
                                                className={`w-full h-[42px] appearance-none rounded-[14px] border ${
                                                    errors.tempoEmpresa ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                                } bg-white px-4 py-2 pr-8 text-sm ${
                                                    formData.tempoEmpresa ? "text-ink" : "text-ink/40"
                                                } focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                            >
                                                <option value="" disabled>
                                                    Selecione o tempo...
                                                </option>
                                                {TEMPO_OPTIONS.map((opt) => (
                                                    <option key={opt} value={opt} className="text-ink">
                                                        {opt}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink/60">
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>
                                        {errors.tempoEmpresa && (
                                            <p className="mt-1 text-[11px] font-medium text-red-500">{errors.tempoEmpresa}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 2 */}
                    {step === 2 && (
                        <motion.div
                            key="step-2"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 2/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[40%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[28px] sm:text-[32px] font-bold leading-tight text-ink tracking-tight">
                                Qual serviço você precisa?
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Escolha o que melhor se encaixa com sua necessidade. Não se preocupe se não tiver certeza, a gente ajuda!
                            </p>

                            <div className="space-y-6">
                                <div>
                                    <label className="mb-3 block text-[13px] font-medium text-ink">
                                        Selecione o serviço desejado: *
                                    </label>
                                    <div className="flex flex-wrap gap-2.5">
                                        {SERVICES_OPTIONS.map((servico) => {
                                            const isSelected = formData.servico === servico;
                                            return (
                                                <button
                                                    key={servico}
                                                    type="button"
                                                    onClick={() => selectServico(servico)}
                                                    className={`rounded-full border px-4 py-2 text-[13px] sm:text-sm font-medium transition-all cursor-pointer ${
                                                        isSelected
                                                            ? "border-brand bg-brand text-white shadow-sm"
                                                            : "border-brand bg-white text-ink hover:bg-brand/10"
                                                    }`}
                                                >
                                                    {servico}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {errors.servico && (
                                        <p className="mt-2 text-[11px] font-medium text-red-500">{errors.servico}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Budget disponível *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="R$ 1.000"
                                        value={formData.budget}
                                        onChange={handleBudgetChange}
                                        className={`w-full rounded-[14px] border ${
                                            errors.budget ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.budget && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.budget}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 3 — BRAND CARE */}
                    {step === 3 && formData.servico === "Brand Care" && (
                        <motion.div
                            key="step-3-brand-care"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 3/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[60%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[26px] sm:text-[30px] font-bold leading-tight text-ink tracking-tight">
                                Vamos estruturar seu Brand Care
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Brand Care é nosso serviço de gerenciamento completo de comunicação. Vamos entender sua situação atual.
                            </p>

                            <div className="space-y-5">
                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Sua marca já tem identidade visual definida? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {["Sim, está consolidada", "Sim, mas precisa atualizar/rebranding", "Não, vamos criar do zero"].map((opt) => (
                                            <RadioItem
                                                key={opt}
                                                label={opt}
                                                selected={formData.brandCare_identidade === opt}
                                                onSelect={() => updateField("brandCare_identidade", opt)}
                                            />
                                        ))}
                                    </div>
                                    {errors.brandCare_identidade && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.brandCare_identidade}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Com quais redes sociais você trabalha atualmente? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {["Instagram", "Facebook", "LinkedIn", "TikTok"].map((rede) => (
                                            <CheckboxItem
                                                key={rede}
                                                label={rede}
                                                checked={formData.brandCare_redes.includes(rede)}
                                                onToggle={() => toggleArrayField("brandCare_redes", rede)}
                                            />
                                        ))}
                                        <div className="flex items-center gap-2 pt-0.5">
                                            <span className="text-[13px] text-ink/80">Outra:</span>
                                            <input
                                                type="text"
                                                value={formData.brandCare_redesOutra}
                                                onChange={(e) => updateField("brandCare_redesOutra", e.target.value)}
                                                placeholder="Digite outra rede social"
                                                className="flex-1 border-b border-ink/30 bg-transparent px-1 py-0.5 text-xs text-ink focus:border-brand focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    {errors.brandCare_redes && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.brandCare_redes}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Qual é seu público-alvo principal? *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Descreva quem é seu cliente ideal"
                                        value={formData.brandCare_publico}
                                        onChange={(e) => updateField("brandCare_publico", e.target.value)}
                                        className={`w-full rounded-[14px] border ${
                                            errors.brandCare_publico ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.brandCare_publico && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.brandCare_publico}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 3 — IDENTIDADE VISUAL */}
                    {step === 3 && formData.servico === "Identidade Visual" && (
                        <motion.div
                            key="step-3-id-visual"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 3/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[60%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[26px] sm:text-[30px] font-bold leading-tight text-ink tracking-tight">
                                Vamos criar/repensar sua identidade visual
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Identidade Visual é logo, cores, tipografia e elementos. Agora, precisamos entender a sua situação.
                            </p>

                            <div className="space-y-5">
                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Você já tem identidade visual? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {[
                                            "Sim, quero fazer rebranding completo",
                                            "Sim, mas apenas atualizações pontuais",
                                            "Não, vou criar do zero",
                                            "Tenho logo, mas precisa evoluir tudo",
                                        ].map((opt) => (
                                            <RadioItem
                                                key={opt}
                                                label={opt}
                                                selected={formData.idVisual_situacao === opt}
                                                onSelect={() => updateField("idVisual_situacao", opt)}
                                            />
                                        ))}
                                    </div>
                                    {errors.idVisual_situacao && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.idVisual_situacao}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Qual é o escopo desejado? *
                                    </p>
                                    <div className="space-y-2">
                                        {[
                                            "Identidade Visual básica (Logo, Cores, Tipografia).",
                                            "Identidade Visual intermediária (Logo, Cores, Tipografia, 3 Aplicações).",
                                            "Identidade Visual completa (Logo, Cores, Tipografia, 5 Aplicações, Manual de Marca, Arquivos abertos).",
                                        ].map((escopo) => (
                                            <CheckboxItem
                                                key={escopo}
                                                label={escopo}
                                                checked={formData.idVisual_escopo.includes(escopo)}
                                                onToggle={() => toggleArrayField("idVisual_escopo", escopo)}
                                            />
                                        ))}
                                    </div>
                                    {errors.idVisual_escopo && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.idVisual_escopo}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Há alguma referência? *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Cite marcas, empresas, e/ou pessoas que sirvam de referência."
                                        value={formData.idVisual_referencias}
                                        onChange={(e) => updateField("idVisual_referencias", e.target.value)}
                                        className={`w-full rounded-[14px] border ${
                                            errors.idVisual_referencias ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.idVisual_referencias && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.idVisual_referencias}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 3 — PACKPOST */}
                    {step === 3 && formData.servico === "PackPost" && (
                        <motion.div
                            key="step-3-packpost"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 3/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[60%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[26px] sm:text-[30px] font-bold leading-tight text-ink tracking-tight">
                                Vamos estruturar seu conteúdo mensal
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Pack Post é conteúdo regular para redes sociais. Defina suas necessidades.
                            </p>

                            <div className="space-y-5">
                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Qual rede social é prioridade? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {["Instagram", "Facebook", "LinkedIn", "TikTok"].map((rede) => (
                                            <CheckboxItem
                                                key={rede}
                                                label={rede}
                                                checked={formData.packPost_prioridade.includes(rede)}
                                                onToggle={() => toggleArrayField("packPost_prioridade", rede)}
                                            />
                                        ))}
                                        <div className="flex items-center gap-2 pt-0.5">
                                            <span className="text-[13px] text-ink/80">Outra:</span>
                                            <input
                                                type="text"
                                                value={formData.packPost_prioridadeOutra}
                                                onChange={(e) => updateField("packPost_prioridadeOutra", e.target.value)}
                                                placeholder="Digite outra rede"
                                                className="flex-1 border-b border-ink/30 bg-transparent px-1 py-0.5 text-xs text-ink focus:border-brand focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    {errors.packPost_prioridade && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.packPost_prioridade}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Quantos posts você gostaria por mês? *
                                    </p>
                                    <div className="flex flex-wrap gap-3">
                                        {["5 posts", "10 posts", "15 posts", "20 posts", "20+ posts"].map((qtd) => (
                                            <CheckboxItem
                                                key={qtd}
                                                label={qtd}
                                                checked={formData.packPost_quantidade === qtd}
                                                onToggle={() => updateField("packPost_quantidade", qtd)}
                                            />
                                        ))}
                                    </div>
                                    {errors.packPost_quantidade && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.packPost_quantidade}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Tipo de conteúdo desejado: *
                                    </p>
                                    <div className="space-y-1.5">
                                        {["Posts estáticos (imagens com texto)", "Carrosséis (múltiplas imagens)", "Stories"].map((tipo) => (
                                            <CheckboxItem
                                                key={tipo}
                                                label={tipo}
                                                checked={formData.packPost_tipoConteudo.includes(tipo)}
                                                onToggle={() => toggleArrayField("packPost_tipoConteudo", tipo)}
                                            />
                                        ))}
                                    </div>
                                    {errors.packPost_tipoConteudo && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.packPost_tipoConteudo}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 3.2 — PACKPOST */}
                    {step === 3.2 && formData.servico === "PackPost" && (
                        <motion.div
                            key="step-3-2-packpost"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 3.2/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[70%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[26px] sm:text-[30px] font-bold leading-tight text-ink tracking-tight">
                                Vamos estruturar seu conteúdo mensal
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Pack Post é conteúdo regular para redes sociais. Defina suas necessidades.
                            </p>

                            <div className="space-y-5">
                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Você tem conteúdo/materiais para usar? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {[
                                            "Sim, forneço fotos e textos",
                                            "Temos fotos, precisa de textos",
                                            "Temos alguns materiais",
                                            "Precisa tudo criado (fotos, textos, design)",
                                        ].map((mat) => (
                                            <RadioItem
                                                key={mat}
                                                label={mat}
                                                selected={formData.packPost_materiais.includes(mat)}
                                                onSelect={() => updateField("packPost_materiais", [mat])}
                                            />
                                        ))}
                                    </div>
                                    {errors.packPost_materiais && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.packPost_materiais}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Você já possui identidade visual estabelecida? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {[
                                            "Sim, temos manual completo",
                                            "Temos apenas logo e cores",
                                            "Não, precisamos criar/reformular",
                                        ].map((tem) => (
                                            <RadioItem
                                                key={tem}
                                                label={tem}
                                                selected={formData.packPost_temIdVisual === tem}
                                                onSelect={() => updateField("packPost_temIdVisual", tem)}
                                            />
                                        ))}
                                    </div>
                                    {errors.packPost_temIdVisual && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.packPost_temIdVisual}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Tom de voz da comunicação:
                                    </label>
                                    <select
                                        value={formData.packPost_tom}
                                        onChange={(e) => updateField("packPost_tom", e.target.value)}
                                        className="w-full rounded-[14px] border border-[#F08032] bg-white px-4 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                    >
                                        {TOM_OPTIONS.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Qual seu objetivo principal com as postagens? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {[
                                            "Reconhecimento de marca",
                                            "Atrair novos clientes",
                                            "Engajamento com o público",
                                            "Vender produtos/serviços diretamente",
                                        ].map((obj) => (
                                            <CheckboxItem
                                                key={obj}
                                                label={obj}
                                                checked={formData.packPost_objetivo.includes(obj)}
                                                onToggle={() => toggleArrayField("packPost_objetivo", obj)}
                                            />
                                        ))}
                                    </div>
                                    {errors.packPost_objetivo && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.packPost_objetivo}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 3 — DESIGN GRÁFICO */}
                    {step === 3 && formData.servico === "Design Gráfico" && (
                        <motion.div
                            key="step-3-design-grafico"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 3/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[60%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[26px] sm:text-[30px] font-bold leading-tight text-ink tracking-tight">
                                Qual material você precisa?
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Design Gráfico é criação de materiais específicos. Detalhe sua necessidade.
                            </p>

                            <div className="space-y-5">
                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Qual tipo de material você precisa? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {[
                                            "Banner/Anúncio digital",
                                            "Outdoor/Painel (material impresso grande)",
                                            "Uniformes (camiseta, jaqueta, etc)",
                                            "Materiais corporativos (papel timbrado, cartão de visita, etc)",
                                            "Catálogo/Brochura/Flyer",
                                            "Embalagem/Rótulo",
                                            "Troféu/Brinde personalizado",
                                            "Capa de Livro/Revista",
                                        ].map((mat) => (
                                            <CheckboxItem
                                                key={mat}
                                                label={mat}
                                                checked={formData.design_tipoMaterial.includes(mat)}
                                                onToggle={() => toggleArrayField("design_tipoMaterial", mat)}
                                            />
                                        ))}
                                        <div className="flex items-center gap-2 pt-0.5">
                                            <span className="text-[13px] text-ink/80">Outro:</span>
                                            <input
                                                type="text"
                                                value={formData.design_materialOutro}
                                                onChange={(e) => updateField("design_materialOutro", e.target.value)}
                                                placeholder="Digite outro material desejado"
                                                className="flex-1 border-b border-ink/30 bg-transparent px-1 py-0.5 text-xs text-ink focus:border-brand focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    {errors.design_tipoMaterial && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.design_tipoMaterial}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 3.2 — DESIGN GRÁFICO */}
                    {step === 3.2 && formData.servico === "Design Gráfico" && (
                        <motion.div
                            key="step-3-2-design-grafico"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 3.2/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[70%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[26px] sm:text-[30px] font-bold leading-tight text-ink tracking-tight">
                                Qual material você precisa?
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Design Gráfico é criação de materiais específicos. Detalhe sua necessidade.
                            </p>

                            <div className="space-y-5">
                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Você tem referências ou brief? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {[
                                            "Sim, tenho projeto bem definido",
                                            "Tenho uma ideia, precisa desenvolvê-la",
                                            "Não, preciso de um alinhamento com vocês",
                                        ].map((opt) => (
                                            <RadioItem
                                                key={opt}
                                                label={opt}
                                                selected={formData.design_temBrief === opt}
                                                onSelect={() => updateField("design_temBrief", opt)}
                                            />
                                        ))}
                                    </div>
                                    {errors.design_temBrief && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.design_temBrief}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Sua marca tem identidade visual? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {[
                                            "Sim, usa identidade estabelecida",
                                            "Sim, mas pode adequar",
                                            "Não, fazemos conforme criatividade",
                                        ].map((opt) => (
                                            <RadioItem
                                                key={opt}
                                                label={opt}
                                                selected={formData.design_temIdVisual === opt}
                                                onSelect={() => updateField("design_temIdVisual", opt)}
                                            />
                                        ))}
                                    </div>
                                    {errors.design_temIdVisual && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.design_temIdVisual}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 3 — PLANEJAMENTO & ESTRATÉGIA / NÃO TENHO CERTEZA AINDA */}
                    {step === 3 && (formData.servico === "Planejamento & Estratégia" || formData.servico === "Não tenho certeza ainda") && (
                        <motion.div
                            key="step-3-planejamento"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 3/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[60%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[26px] sm:text-[30px] font-bold leading-tight text-ink tracking-tight">
                                Vamos conversar sobre sua marca
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Uma consultoria estratégica vai ajudar a definir exatamente o que você precisa.
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Qual é sua principal dúvida/dificuldade? *
                                    </label>
                                    <textarea
                                        rows={3}
                                        placeholder="Responda livremente..."
                                        value={formData.plan_dificuldade}
                                        onChange={(e) => updateField("plan_dificuldade", e.target.value)}
                                        className={`w-full resize-none rounded-[14px] border ${
                                            errors.plan_dificuldade ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.plan_dificuldade && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.plan_dificuldade}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Sua marca já tem identidade visual definida? *
                                    </p>
                                    <div className="space-y-1.5">
                                        {["Sim, está consolidada", "Sim, mas precisa atualizar/rebranding", "Não, vamos criar do zero"].map((opt) => (
                                            <RadioItem
                                                key={opt}
                                                label={opt}
                                                selected={formData.plan_temIdVisual === opt}
                                                onSelect={() => updateField("plan_temIdVisual", opt)}
                                            />
                                        ))}
                                    </div>
                                    {errors.plan_temIdVisual && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.plan_temIdVisual}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Qual é seu maior objetivo nos próximos 6 meses? *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Responda livremente..."
                                        value={formData.plan_objetivo6m}
                                        onChange={(e) => updateField("plan_objetivo6m", e.target.value)}
                                        className={`w-full rounded-[14px] border ${
                                            errors.plan_objetivo6m ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.plan_objetivo6m && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.plan_objetivo6m}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Qual é seu público-alvo? *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Descreva o seu cliente ideal"
                                        value={formData.plan_publico}
                                        onChange={(e) => updateField("plan_publico", e.target.value)}
                                        className={`w-full rounded-[14px] border ${
                                            errors.plan_publico ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.plan_publico && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.plan_publico}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 3.2 — PLANEJAMENTO & ESTRATÉGIA / NÃO TENHO CERTEZA AINDA */}
                    {step === 3.2 && (formData.servico === "Planejamento & Estratégia" || formData.servico === "Não tenho certeza ainda") && (
                        <motion.div
                            key="step-3-2-planejamento"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 3.2/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[70%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[26px] sm:text-[30px] font-bold leading-tight text-ink tracking-tight">
                                Vamos conversar sobre sua marca
                            </h1>
                            <p className="mt-1 mb-6 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Uma consultoria estratégica vai ajudar a definir exatamente o que você precisa.
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Cite 3 dos seus maiores concorrentes (inclua @ do Instagram): *
                                    </p>
                                    <div className="space-y-2">
                                        <input
                                            type="text"
                                            placeholder="Concorrente 1 + @instagram"
                                            value={formData.plan_concorrente1}
                                            onChange={(e) => updateField("plan_concorrente1", e.target.value)}
                                            className="w-full rounded-[14px] border border-[#F08032] bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Concorrente 2 + @instagram"
                                            value={formData.plan_concorrente2}
                                            onChange={(e) => updateField("plan_concorrente2", e.target.value)}
                                            className="w-full rounded-[14px] border border-[#F08032] bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Concorrente 3 + @instagram"
                                            value={formData.plan_concorrente3}
                                            onChange={(e) => updateField("plan_concorrente3", e.target.value)}
                                            className="w-full rounded-[14px] border border-[#F08032] bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                        />
                                    </div>
                                    {errors.plan_concorrentes && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.plan_concorrentes}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-1 block text-[13px] font-medium text-ink">
                                        Como seus clientes te encontram atualmente? *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Responda livremente..."
                                        value={formData.plan_comoEncontram}
                                        onChange={(e) => updateField("plan_comoEncontram", e.target.value)}
                                        className={`w-full rounded-[14px] border ${
                                            errors.plan_comoEncontram ? "border-red-500 ring-1 ring-red-500" : "border-[#F08032]"
                                        } bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`}
                                    />
                                    {errors.plan_comoEncontram && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.plan_comoEncontram}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="type-display rounded-[12px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] cursor-pointer"
                                >
                                    Próximo
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 4 */}
                    {step === 4 && (
                        <motion.div
                            key="step-4"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-[28px] sm:rounded-[32px] bg-white p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] text-ink"
                        >
                            <div className="text-[13px] font-medium text-ink/60">Etapa 4/5</div>
                            <div className="mt-2 mb-6 h-[3.5px] w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                                <div className="h-full w-[80%] rounded-full bg-brand transition-all duration-300" />
                            </div>

                            <h1 className="type-display text-[32px] sm:text-[38px] font-bold leading-tight text-brand tracking-tight">
                                Tudo Pronto!!!!!
                            </h1>
                            <p className="mt-1 mb-5 text-[12px] sm:text-[13px] leading-relaxed text-[#6B7280]">
                                Vamos revisar as informações e você confirma para enviar.
                            </p>

                            {/* Resumo automático das respostas principais */}
                            <div className="mb-6 rounded-[14px] border border-ink/10 bg-[#F9FAFB] p-4 text-[13px] space-y-2">
                                <div className="font-semibold text-ink">
                                    {formData.nome || "Nome não informado"}
                                    {formData.empresa ? ` • ${formData.empresa}` : ""}
                                </div>
                                <div className="text-ink/70">
                                    <span>E-mail: {formData.email || "—"}</span>
                                    <span className="mx-1.5">•</span>
                                    <span>Tel: {formData.telefone || "—"}</span>
                                </div>
                                <div className="text-ink/70">
                                    <span>Cargo: {formData.cargo}</span>
                                    <span className="mx-1.5">•</span>
                                    <span>Tempo: {formData.tempoEmpresa}</span>
                                </div>
                                <div className="pt-1 border-t border-ink/10 text-ink/80">
                                    <span className="font-medium text-ink">Serviço: </span>
                                    {formData.servico || "A definir"}
                                    {formData.budget ? ` • Budget: ${formData.budget}` : ""}
                                </div>

                                {/* Resumo dinâmico do briefing específico */}
                                <div className="pt-1 border-t border-ink/10 text-ink/70 text-[12px] space-y-1">
                                    {formData.servico === "Brand Care" && (
                                        <>
                                            <div>Identidade: {formData.brandCare_identidade || "—"}</div>
                                            <div>Redes: {[...formData.brandCare_redes, formData.brandCare_redesOutra].filter(Boolean).join(", ") || "—"}</div>
                                            <div>Público: {formData.brandCare_publico || "—"}</div>
                                        </>
                                    )}
                                    {formData.servico === "Identidade Visual" && (
                                        <>
                                            <div>Situação: {formData.idVisual_situacao || "—"}</div>
                                            <div>Escopo: {formData.idVisual_escopo.join(", ") || "—"}</div>
                                            <div>Referências: {formData.idVisual_referencias || "—"}</div>
                                        </>
                                    )}
                                    {formData.servico === "PackPost" && (
                                        <>
                                            <div>Redes prioritárias: {[...formData.packPost_prioridade, formData.packPost_prioridadeOutra].filter(Boolean).join(", ") || "—"}</div>
                                            <div>Quantidade: {formData.packPost_quantidade || "—"} • Conteúdo: {formData.packPost_tipoConteudo.join(", ") || "—"}</div>
                                            <div>Materiais: {formData.packPost_materiais.join(", ") || "—"} • Tom: {formData.packPost_tom}</div>
                                            <div>Objetivos: {formData.packPost_objetivo.join(", ") || "—"}</div>
                                        </>
                                    )}
                                    {formData.servico === "Design Gráfico" && (
                                        <>
                                            <div>Materiais: {[...formData.design_tipoMaterial, formData.design_materialOutro].filter(Boolean).join(", ") || "—"}</div>
                                            <div>Brief/Ref: {formData.design_temBrief || "—"} • ID Visual: {formData.design_temIdVisual || "—"}</div>
                                        </>
                                    )}
                                    {(formData.servico === "Planejamento & Estratégia" || formData.servico === "Não tenho certeza ainda") && (
                                        <>
                                            <div>Dificuldade: {formData.plan_dificuldade || "—"}</div>
                                            <div>Objetivo 6m: {formData.plan_objetivo6m || "—"} • Público: {formData.plan_publico || "—"}</div>
                                            <div>Concorrentes: {[formData.plan_concorrente1, formData.plan_concorrente2, formData.plan_concorrente3].filter(Boolean).join(", ") || "—"}</div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Checkboxes de Confirmação */}
                            <div className="space-y-4">
                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Revise os dados acima e confirme se está tudo correto: *
                                    </p>
                                    <label className="flex items-start gap-2.5 cursor-pointer text-[13px] text-ink/80">
                                        <input
                                            type="checkbox"
                                            checked={formData.confirmoDados}
                                            onChange={(e) => updateField("confirmoDados", e.target.checked)}
                                            className="mt-0.5 h-4 w-4 rounded border-[#F08032] text-brand focus:ring-brand cursor-pointer"
                                        />
                                        <span>Confirmo que as informações estão corretas</span>
                                    </label>
                                    {errors.confirmoDados && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.confirmoDados}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-[13px] font-medium text-ink">
                                        Autorizo que a Rockfox Studio entre em contato via email/WhatsApp: *
                                    </p>
                                    <label className="flex items-start gap-2.5 cursor-pointer text-[13px] text-ink/80">
                                        <input
                                            type="checkbox"
                                            checked={formData.autorizoContato}
                                            onChange={(e) => updateField("autorizoContato", e.target.checked)}
                                            className="mt-0.5 h-4 w-4 rounded border-[#F08032] text-brand focus:ring-brand cursor-pointer"
                                        />
                                        <span>Sim, podem contatar</span>
                                    </label>
                                    {errors.autorizoContato && (
                                        <p className="mt-1 text-[11px] font-medium text-red-500">{errors.autorizoContato}</p>
                                    )}
                                </div>
                            </div>

                            {errorMessage && (
                                <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-[13px] text-red-600">
                                    {errorMessage}
                                </div>
                            )}

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="type-display rounded-[12px] bg-[#8C9398] px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#787e83] active:scale-[0.98] cursor-pointer"
                                >
                                    Voltar
                                </button>
                                <button
                                    type="button"
                                    disabled={status === "loading"}
                                    onClick={handleSubmitBriefing}
                                    className="type-display rounded-[14px] bg-brand px-7 py-2.5 text-sm sm:text-base font-bold text-white transition-all hover:bg-[#d55900] active:scale-[0.98] disabled:opacity-50 cursor-pointer"
                                >
                                    {status === "loading" ? "Enviando..." : "Enviar Briefing"}
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ETAPA 5 */}
                    {step === 5 && (
                        <motion.div
                            key="step-5"
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.35 }}
                            className="rounded-[28px] sm:rounded-[36px] bg-gradient-to-b from-[#EE6211] via-[#DE5506] to-[#BA3E00] p-7 sm:p-9 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.35)] text-white"
                        >
                            <div className="text-[13px] font-medium text-white/90">Etapa 5/5</div>
                            <div className="mt-2.5 mb-6 h-[3px] w-full rounded-full bg-white" />

                            <h1 className="type-display text-[32px] sm:text-[40px] font-bold leading-[1.05] tracking-tight text-white">
                                Perfeito!
                            </h1>
                            <h2 className="type-display mt-1 text-[28px] sm:text-[36px] font-bold leading-[1.1] tracking-tight text-white">
                                Recebemos seu briefing.
                            </h2>

                            <p className="mt-4 text-[14px] sm:text-[15px] leading-relaxed text-white/95">
                                Vamos analisar e enviar uma proposta personalizada em até 3 dias úteis.<br className="hidden sm:inline" /> Fique ligado no seu email!
                            </p>

                            <div className="mt-8 pt-4">
                                <p className="text-[13px] sm:text-[14px] text-white/80">
                                    Dúvidas? Enviar e-mail para:
                                </p>
                                <a
                                    href="mailto:adm@rockfoxstudio.com"
                                    className="mt-1 inline-block text-[15px] sm:text-[16px] font-bold text-white transition-opacity hover:opacity-85 no-underline"
                                >
                                    adm@rockfoxstudio.com
                                </a>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

